import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { resolveLandingAssetUrl } from "../../../../utils/landingAssets";
import { HighlightCanvasIcon } from "../../landingEditor/canvas/LandingCanvasPrimitives";
import { getValueAtDescriptorPath } from "../elementEditing";
import { HIGHLIGHT_ICON_OPTIONS } from "../registry/segmentRegistryCore";
import ImageElementEditor from "./ImageElementEditor";
import TextElementEditor from "./TextElementEditor";

function collectionProperty(descriptor) {
  return descriptor?.property || descriptor?.collection?.property || null;
}

function fieldDefinition(property, key) {
  return (property?.item?.fields || []).find((field) => field?.key === key) || null;
}

function moveItem(items, from, to) {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function useCollectionState({ descriptor, form, disabled, onCommit, createItem }) {
  const property = collectionProperty(descriptor);
  const collectionPath = property?.path || "";
  const rawItems = collectionPath ? getValueAtDescriptorPath(form, collectionPath, []) : [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const maxItems = Number.isFinite(Number(property?.limits?.maxItems)) ? Number(property.limits.maxItems) : 40;
  const directIndex = descriptor?.kind === "collection-item" && Number.isInteger(descriptor?.collection?.index)
    ? descriptor.collection.index
    : null;
  const [selectedIndex, setSelectedIndex] = React.useState(directIndex);

  React.useEffect(() => {
    setSelectedIndex(directIndex);
  }, [collectionPath, directIndex]);

  const commitItems = (next) => onCommit?.(collectionPath, next.slice(0, maxItems));
  const addItem = () => {
    if (disabled || items.length >= maxItems) return;
    const next = [...items, createItem(items.length)];
    commitItems(next);
    setSelectedIndex(next.length - 1);
  };
  const removeItem = (index) => {
    if (disabled || index < 0 || index >= items.length) return;
    commitItems(items.filter((_, itemIndex) => itemIndex !== index));
    setSelectedIndex(null);
  };
  const reorder = (from, to) => {
    if (disabled || to < 0 || to >= items.length) return;
    commitItems(moveItem(items, from, to));
    if (selectedIndex === from) setSelectedIndex(to);
    else if (selectedIndex === to) setSelectedIndex(from);
  };

  return {
    property,
    collectionPath,
    items,
    maxItems,
    selectedIndex,
    setSelectedIndex,
    addItem,
    removeItem,
    reorder,
  };
}

function ReorderControls({ label, index, total, disabled, onMoveUp, onMoveDown }) {
  return (
    <Stack direction="row" justifyContent="center" spacing={0.25}>
      <Tooltip title="Mover antes">
        <span>
          <IconButton size="small" disabled={disabled || index === 0} onClick={onMoveUp} aria-label={`Mover ${label} antes`}>
            <KeyboardArrowUpRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Mover después">
        <span>
          <IconButton size="small" disabled={disabled || index >= total - 1} onClick={onMoveDown} aria-label={`Mover ${label} después`}>
            <KeyboardArrowDownRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function displayBrandName(item, index) {
  return String(item?.name ?? item?.label ?? "").trim() || `Marca ${index + 1}`;
}

function brandSearchText(item) {
  return [item?.name, item?.label, item?.alt, item?.query]
    .map((value) => String(value || "").toLocaleLowerCase())
    .join(" ");
}

function BrandThumb({ item, index, disabled, total, onOpen, onMoveUp, onMoveDown }) {
  const logo = resolveLandingAssetUrl(item?.logoUrl);
  const name = displayBrandName(item, index);
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2.2, borderColor: "rgba(25,36,47,.12)", bgcolor: "#fff" }}>
      <Button
        fullWidth
        disabled={disabled}
        onClick={onOpen}
        aria-label={`Editar ${name}`}
        sx={{ minHeight: 86, px: 0.8, py: 0.7, display: "flex", flexDirection: "column", gap: 0.6, textTransform: "none", color: "inherit", borderRadius: 1.5 }}
      >
        <Box sx={{ width: "100%", height: 48, display: "grid", placeItems: "center", borderRadius: 1.3, bgcolor: "rgba(25,36,47,.035)", overflow: "hidden" }}>
          {logo ? (
            <Box component="img" src={logo} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <Typography variant="caption" sx={{ px: 0.5, fontWeight: 900, textAlign: "center", color: "rgba(25,36,47,.72)" }}>{name}</Typography>
          )}
        </Box>
        <Typography variant="caption" noWrap sx={{ width: "100%", fontWeight: 900 }} title={name}>{name}</Typography>
      </Button>
      <ReorderControls label={name} index={index} total={total} disabled={disabled} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
    </Paper>
  );
}

function BrandsCollectionEditor({ descriptor, form, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const state = useCollectionState({
    descriptor,
    form,
    disabled,
    onCommit,
    createItem: () => ({ name: "", logoUrl: "", alt: "", query: "" }),
  });
  const { property, collectionPath, items, maxItems, selectedIndex, setSelectedIndex, addItem, removeItem, reorder } = state;
  const [query, setQuery] = React.useState("");

  React.useEffect(() => setQuery(""), [collectionPath]);

  if (!property) return null;

  if (Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < items.length) {
    const item = items[selectedIndex] || {};
    const nameDefinition = fieldDefinition(property, "name");
    const logoDefinition = fieldDefinition(property, "logoUrl");
    const altDefinition = fieldDefinition(property, "alt");
    const queryDefinition = fieldDefinition(property, "query");
    const name = displayBrandName(item, selectedIndex);
    const logoPath = `${collectionPath}.${selectedIndex}.logoUrl`;
    const altPath = `${collectionPath}.${selectedIndex}.alt`;
    const imageDescriptor = logoDefinition ? {
      ...descriptor,
      kind: "image",
      label: "Logo",
      fieldPath: logoPath,
      canvasPath: logoPath,
      field: { ...logoDefinition, path: logoPath, label: "Logo" },
      altField: altDefinition ? { ...altDefinition, path: altPath, label: "Texto alternativo" } : null,
      collection: { property, index: selectedIndex, itemKey: "logoUrl", itemField: logoDefinition },
      chips: [`Marca ${selectedIndex + 1}`],
    } : null;

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Button size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => setSelectedIndex(null)} sx={{ textTransform: "none", fontWeight: 900 }}>Volver a marcas</Button>
          <Chip size="small" label={`${selectedIndex + 1} / ${items.length}`} variant="outlined" />
        </Stack>
        <Box>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{name}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>Logo, nombre y búsqueda de esta marca.</Typography>
        </Box>
        {imageDescriptor ? <ImageElementEditor descriptor={imageDescriptor} form={form} disabled={disabled} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} /> : null}
        <TextField fullWidth size="small" label="Nombre" value={item?.name ?? item?.label ?? ""} disabled={disabled} inputProps={{ maxLength: nameDefinition?.maxLength }} onChange={(event) => onCommit?.(`${collectionPath}.${selectedIndex}.name`, event.target.value)} />
        <TextField fullWidth size="small" label="Búsqueda al abrir" value={item?.query ?? ""} disabled={disabled} inputProps={{ maxLength: queryDefinition?.maxLength }} onChange={(event) => onCommit?.(`${collectionPath}.${selectedIndex}.query`, event.target.value)} helperText="Si queda vacío, se usa el nombre de la marca." />
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Button size="small" variant="outlined" disabled={disabled || selectedIndex === 0} startIcon={<KeyboardArrowUpRoundedIcon />} onClick={() => reorder(selectedIndex, selectedIndex - 1)} sx={{ textTransform: "none", fontWeight: 850 }}>Mover antes</Button>
          <Button size="small" variant="outlined" disabled={disabled || selectedIndex >= items.length - 1} startIcon={<KeyboardArrowDownRoundedIcon />} onClick={() => reorder(selectedIndex, selectedIndex + 1)} sx={{ textTransform: "none", fontWeight: 850 }}>Mover después</Button>
          <Button size="small" color="error" disabled={disabled} startIcon={<DeleteOutlineRoundedIcon />} onClick={() => removeItem(selectedIndex)} sx={{ textTransform: "none", fontWeight: 850, ml: { sm: "auto" } }}>Eliminar</Button>
        </Stack>
      </Stack>
    );
  }

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleItems = items
    .map((item, index) => ({ item: item || {}, index }))
    .filter(({ item }) => !normalizedQuery || brandSearchText(item).includes(normalizedQuery));

  return (
    <Stack spacing={1.4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Marcas</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>Tocá un logo para editarlo.</Typography>
        </Box>
        <Chip size="small" label={`${items.length} / ${maxItems}`} color={items.length >= maxItems ? "warning" : "default"} />
      </Stack>
      {items.length > 6 ? (
        <TextField fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar en tus marcas" inputProps={{ "aria-label": "Buscar en marcas configuradas" }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
      ) : null}
      {visibleItems.length ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
          {visibleItems.map(({ item, index }) => (
            <BrandThumb key={`brand-editor:${index}:${item?.name || item?.label || ""}`} item={item} index={index} total={items.length} disabled={disabled} onOpen={() => setSelectedIndex(index)} onMoveUp={() => reorder(index, index - 1)} onMoveDown={() => reorder(index, index + 1)} />
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 2, textAlign: "center", border: "1px dashed rgba(25,36,47,.18)", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 850, color: "rgba(25,36,47,.72)" }}>{items.length ? "No hay marcas que coincidan." : "Todavía no agregaste marcas."}</Typography>
        </Box>
      )}
      <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={disabled || items.length >= maxItems} onClick={() => { setQuery(""); addItem(); }} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>Agregar marca</Button>
    </Stack>
  );
}

function displayHighlightName(item, index) {
  return String(item?.title || "").trim() || `Diferencial ${index + 1}`;
}

function HighlightThumb({ item, index, total, disabled, onOpen, onMoveUp, onMoveDown }) {
  const image = resolveLandingAssetUrl(item?.imageUrl);
  const name = displayHighlightName(item, index);
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2.2, borderColor: "rgba(25,36,47,.12)", bgcolor: "#fff" }}>
      <Button
        fullWidth
        disabled={disabled}
        onClick={onOpen}
        aria-label={`Editar ${name}`}
        sx={{ minHeight: 104, p: 0.7, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.6, textTransform: "none", color: "inherit", borderRadius: 1.5 }}
      >
        <Box sx={{ height: 52, borderRadius: 1.4, overflow: "hidden", display: "grid", placeItems: "center", bgcolor: "rgba(25,36,47,.045)" }}>
          {image ? (
            <Box component="img" src={image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : item?.icon ? (
            <HighlightCanvasIcon name={item.icon} sx={{ fontSize: 30, color: "primary.main" }} />
          ) : (
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 850 }}>Sin imagen</Typography>
          )}
        </Box>
        <Typography variant="caption" noWrap sx={{ width: "100%", fontWeight: 950, textAlign: "left" }} title={name}>{name}</Typography>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {item?.youtubeUrl ? <Chip size="small" label="Video" variant="outlined" /> : null}
          {item?.imageUrl ? <Chip size="small" label="Imagen" variant="outlined" /> : null}
        </Stack>
      </Button>
      <ReorderControls label={name} index={index} total={total} disabled={disabled} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
    </Paper>
  );
}

function makeHighlightTextDescriptor(descriptor, property, index, key, label) {
  const definition = fieldDefinition(property, key);
  if (!definition) return null;
  const path = `${property.path}.${index}.${key}`;
  return {
    ...descriptor,
    kind: "text",
    label,
    fieldPath: path,
    canvasPath: path,
    field: { ...definition, path, label },
    collection: { property, index, itemKey: key, itemField: definition },
  };
}

function makeHighlightImageDescriptor(descriptor, property, index) {
  const imageDefinition = fieldDefinition(property, "imageUrl");
  if (!imageDefinition) return null;
  const altDefinition = fieldDefinition(property, "imageAlt");
  const positionDefinition = fieldDefinition(property, "imagePosition");
  const imagePath = `${property.path}.${index}.imageUrl`;
  const altPath = `${property.path}.${index}.imageAlt`;
  const positionPath = `${property.path}.${index}.imagePosition`;
  return {
    ...descriptor,
    kind: "image",
    label: "Imagen",
    fieldPath: imagePath,
    canvasPath: imagePath,
    field: {
      ...imageDefinition,
      path: imagePath,
      label: "Imagen",
      altPath,
      positionPath,
      positionOptions: positionDefinition?.options || [],
    },
    altField: altDefinition ? { ...altDefinition, path: altPath, label: "Texto alternativo" } : null,
    collection: { property, index, itemKey: "imageUrl", itemField: imageDefinition },
  };
}

function HighlightsCollectionEditor({ descriptor, form, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const state = useCollectionState({
    descriptor,
    form,
    disabled,
    onCommit,
    createItem: (index) => ({
      id: `diferencial-${Date.now().toString(36)}-${index + 1}`,
      icon: "",
      title: "",
      description: "",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
    }),
  });
  const { property, collectionPath, items, maxItems, selectedIndex, setSelectedIndex, addItem, removeItem, reorder } = state;
  if (!property) return null;

  if (Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < items.length) {
    const item = items[selectedIndex] || {};
    const titleDescriptor = makeHighlightTextDescriptor(descriptor, property, selectedIndex, "title", "Título");
    const descriptionDescriptor = makeHighlightTextDescriptor(descriptor, property, selectedIndex, "description", "Descripción");
    const imageDescriptor = makeHighlightImageDescriptor(descriptor, property, selectedIndex);
    const iconDefinition = fieldDefinition(property, "icon");
    const youtubeDefinition = fieldDefinition(property, "youtubeUrl");
    const itemName = displayHighlightName(item, selectedIndex);

    return (
      <Stack spacing={1.6}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Button size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => setSelectedIndex(null)} sx={{ textTransform: "none", fontWeight: 900 }}>Volver a diferenciales</Button>
          <Chip size="small" label={`${selectedIndex + 1} / ${items.length}`} variant="outlined" />
        </Stack>

        <Box>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{itemName}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>Editás únicamente esta tarjeta.</Typography>
        </Box>

        <Stack spacing={0.8}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Ícono</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.7 }}>
            {(iconDefinition?.options || HIGHLIGHT_ICON_OPTIONS).map((option) => {
              const selected = String(item?.icon || "") === String(option.value || "");
              return (
                <Button
                  key={option.value || "none"}
                  type="button"
                  variant={selected ? "contained" : "outlined"}
                  disabled={disabled}
                  aria-pressed={selected}
                  aria-label={`Ícono ${option.label}`}
                  onClick={() => onCommit?.(`${collectionPath}.${selectedIndex}.icon`, option.value)}
                  sx={{ minWidth: 0, minHeight: 66, px: 0.5, py: 0.7, display: "flex", flexDirection: "column", gap: 0.35, textTransform: "none", borderRadius: 2 }}
                >
                  {option.value ? <HighlightCanvasIcon name={option.value} sx={{ fontSize: 24 }} /> : <Box sx={{ width: 24, height: 24, borderRadius: "50%", border: "1px dashed currentColor", opacity: 0.6 }} />}
                  <Typography variant="caption" sx={{ fontSize: ".66rem", lineHeight: 1.05, fontWeight: 850, textAlign: "center" }}>{option.label}</Typography>
                </Button>
              );
            })}
          </Box>
        </Stack>

        {titleDescriptor ? <TextElementEditor descriptor={titleDescriptor} form={form} disabled={disabled} onCommit={onCommit} /> : null}
        {descriptionDescriptor ? <TextElementEditor descriptor={descriptionDescriptor} form={form} disabled={disabled} onCommit={onCommit} /> : null}

        <Stack spacing={1.1} sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Imagen o video</Typography>
          {!item?.imageUrl && !item?.youtubeUrl ? (
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>Podés sumar una imagen o pegar un video de YouTube.</Typography>
          ) : null}
          {imageDescriptor ? <ImageElementEditor descriptor={imageDescriptor} form={form} disabled={disabled} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} /> : null}
          <TextField
            fullWidth
            size="small"
            label="Video de YouTube"
            value={item?.youtubeUrl || ""}
            disabled={disabled}
            inputProps={{ maxLength: youtubeDefinition?.maxLength }}
            placeholder="https://youtube.com/..."
            onChange={(event) => onCommit?.(`${collectionPath}.${selectedIndex}.youtubeUrl`, event.target.value)}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Button size="small" variant="outlined" disabled={disabled || selectedIndex === 0} startIcon={<KeyboardArrowUpRoundedIcon />} onClick={() => reorder(selectedIndex, selectedIndex - 1)} sx={{ textTransform: "none", fontWeight: 850 }}>Mover antes</Button>
          <Button size="small" variant="outlined" disabled={disabled || selectedIndex >= items.length - 1} startIcon={<KeyboardArrowDownRoundedIcon />} onClick={() => reorder(selectedIndex, selectedIndex + 1)} sx={{ textTransform: "none", fontWeight: 850 }}>Mover después</Button>
          <Button size="small" color="error" disabled={disabled} startIcon={<DeleteOutlineRoundedIcon />} onClick={() => removeItem(selectedIndex)} sx={{ textTransform: "none", fontWeight: 850, ml: { sm: "auto" } }}>Eliminar</Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Diferenciales</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>Tocá una tarjeta para editarla.</Typography>
        </Box>
        <Chip size="small" label={`${items.length} / ${maxItems}`} color={items.length >= maxItems ? "warning" : "default"} />
      </Stack>

      {items.length ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
          {items.map((item, index) => (
            <HighlightThumb key={item?.id || `highlight-editor:${index}`} item={item || {}} index={index} total={items.length} disabled={disabled} onOpen={() => setSelectedIndex(index)} onMoveUp={() => reorder(index, index - 1)} onMoveDown={() => reorder(index, index + 1)} />
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 2, textAlign: "center", border: "1px dashed rgba(25,36,47,.18)", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 850, color: "rgba(25,36,47,.72)" }}>Todavía no agregaste diferenciales.</Typography>
        </Box>
      )}

      <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={disabled || items.length >= maxItems} onClick={addItem} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>Agregar diferencial</Button>
    </Stack>
  );
}

export default function CollectionElementEditor(props) {
  const property = collectionProperty(props.descriptor);
  if (!property) return null;
  if (property.path === "catalog.brands.items") return <BrandsCollectionEditor {...props} />;
  if (property.path === "highlights") return <HighlightsCollectionEditor {...props} />;
  return (
    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.68)" }}>
      Esta colección todavía no usa un editor visual contextual.
    </Typography>
  );
}
